'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

type SportinfoRedirectProps = {
  href: string;
};

/**
 * Normalizes `NEXT_PUBLIC_BASE_PATH` so it always starts with `/` and never ends with `/`,
 * regardless of how it is configured (e.g. `nova-powerplay`, `/powerplay`, or `/powerplay/`).
 *
 * @returns the normalized base path, or an empty string when none is configured
 */
function getBasePath(): string {
  const rawBasePath = process.env.NEXT_PUBLIC_BASE_PATH?.trim() ?? '';
  const trimmedBasePath = rawBasePath.replace(/\/+$/, '');

  return trimmedBasePath === '' || trimmedBasePath.startsWith('/') ? trimmedBasePath : `/${trimmedBasePath}`;
}

/**
 * Redirects invalid sportinfo routes after client-side hydration.
 *
 * @param root0 component props
 * @param root0.href validated fallback route, without base path
 * @returns Nothing - this component only triggers a client-side redirect.
 */
export default function SportinfoRedirect({ href }: SportinfoRedirectProps): undefined {
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const [pathname, hrefSearch = ''] = href.split('?');

    // Keep query params already on the page (e.g. `modal=stats`) and only override the ones the fallback route defines.
    const mergedParams = new URLSearchParams(searchParams);
    for (const [key, value] of new URLSearchParams(hrefSearch)) {
      mergedParams.set(key, value);
    }

    const mergedSearch = mergedParams.toString();
    const target = `${getBasePath()}${pathname}${mergedSearch ? `?${mergedSearch}` : ''}`;

    router.replace(target);
  }, [href, router, searchParams]);

  return undefined;
}
